import * as classnames from "classnames";
import * as React from "react";

import {
  Form,
  FormGroup,
  Input,
  Button,
  Loading,
  QuickHelp,
  Modal,
  Label,
  Msg,
} from "../../../shared/components";
import { IdDeliverySharingModel } from "../../../shared/models";
import {
  dayjs,
  formatDateTimeMinutes,
  getTimeZoneString,
  formatTimeLeftString,
} from "../../../shared/services/date";
import History from "../../../shared/services/history";
import MSG from "../../../shared/services/message";
import { parseUrl } from "../../../shared/services/queryString";
import Error from "../../common/error/error/Error";

/**
 * Prop interface
 */
export interface ExamEntranceIdProps {
  error: boolean;
  loading: boolean;
  initial: IdDeliverySharingModel;
  pageLanguage: string;
  examUrlToken: string;
  passwordRequired: boolean;
  passwordInitialized: boolean;
  sendId: (token: string, id: string) => void;
  setPassword: (token: string, params: {}) => void;
  signin: (token: string, params: {}) => void;
  location?: {
    search: string;
  };
}

/**
 * State interface
 */
export interface ExamEntranceIdState {
  id: string;
  formValid: boolean;

  isModalOpen: boolean;

  passwordFormValid: boolean;
  passwordValues: {};
}

/**
 * Page component
 */
class ExamEntranceId extends React.Component<
  ExamEntranceIdProps,
  ExamEntranceIdState
> {
  constructor(props: ExamEntranceIdProps) {
    super(props);

    this.state = {
      id: "",
      formValid: false,
      isModalOpen: props.passwordRequired,
      passwordFormValid: false,
      passwordValues: {},
    };
  }

  public componentDidUpdate(prevProps: ExamEntranceIdProps) {
    if (this.props.examUrlToken && this.props.examUrlToken.length) {
      // set url to exams url and reload
      // need to reload because the data fetching is dont in the indexUsers.ts file, not within the routes.
      // bit annoying.
      History.push(`../exams/${this.props.examUrlToken}`);
      window.location.reload();
    }
    if (this.props.passwordRequired && !prevProps.passwordRequired) {
      this.setState({
        isModalOpen: this.props.passwordRequired,
      });
    }
  }

  public render() {
    if (this.props.error) {
      return (
        <Error
          hasBackButton={false}
          location={{
            state: {
              error: MSG.getMessageByKey("message.applicant.examNotFound"),
            },
          }}
        />
      );
    }

    if (this.props.initial.isEmpty()) {
      return <Loading isOpen={true} />;
    }

    const rootStyle = classnames("code-exam-entrance");

    const { initial, loading, pageLanguage } = this.props;

    const showTimeLimit =
      initial.idDeliveryConfig.dynamicDeadlineHours &&
      dayjs()
        .add(initial.idDeliveryConfig.dynamicDeadlineHours, "hours")
        .isBefore(dayjs(initial.idDeliveryConfig.endAt));

    // _tid is 'preset id for test delivery'
    const { _tid } = parseUrl(
      this.props.location && this.props.location.search,
    );

    return (
      <div className={rootStyle}>
        <Loading isOpen={loading} />
        <p className="code-exam-entrance__title">
          <Msg id="editor.entrance.title" />
        </p>
        <div className="code-exam-entrance__exam">
          <div className="code-exam-entrance__org">
            <h2 className="code-exam-entrance__orgname">
              <Msg id="common.exam" />
            </h2>
            <p>{initial.getExamName(pageLanguage)}</p>
          </div>
          <div className="code-exam-entrance__deadline">
            <dl>
              <dt>
                <Msg id="delivery.deadline" />
              </dt>
              <dd>
                {formatDateTimeMinutes(initial.idDeliveryConfig.endAt)}
                <br />
                <small>({getTimeZoneString()})</small>
              </dd>
              {(showTimeLimit && (
                <dt>
                  <Msg id="timeLimit" />：
                  <QuickHelp text={MSG.getMessageByKey("timeLimitTooltip")} />
                </dt>
              )) ||
                null}
              {(showTimeLimit && (
                <dd>
                  {formatTimeLeftString(
                    initial.idDeliveryConfig.dynamicDeadlineHours * 3600,
                  )}
                </dd>
              )) ||
                null}
            </dl>
          </div>
        </div>

        {initial.isAvailable() ? (
          <Form
            className="code-exam-entrance__form"
            validation={{
              id: ["string", "required"],
            }}
            initialValues={{
              id: _tid,
            }}
            onFormChange={this.onFormChange}
          >
            <p>
              <Msg id="editor.iddelivery.inputId" />
            </p>
            <FormGroup>
              <Input name="id" disabled={!!_tid} />
            </FormGroup>
            <Button
              type="primary"
              onClick={this.onSendId}
              disabled={!this.state.formValid}
              ariaLabel="Deliver Exam"
            >
              <Msg id="editor.urlDeliverySteps.accessToExam" />
            </Button>
          </Form>
        ) : (
          <div className="code-exam-entrance__expired">
            <Msg id={"editor.deadlineExpired"} />
          </div>
        )}

        <Modal
          isOpen={this.state.isModalOpen}
          onClickCancel={this.onCloseModal}
          onClose={this.onCloseModal}
          disableOk={!this.state.passwordFormValid}
          onClickOk={this.onSubmitPassword}
          title={MSG.getMessageByKey(
            this.props.passwordInitialized
              ? "editor.iddelivery.enterPassword"
              : "editor.iddelivery.setPassword",
          )}
          ariaLabel="Set Password"
        >
          <Form
            validation={{
              password: ["string", "required"],
              newPassword: [
                "string",
                this.props.passwordInitialized ? "optional" : "required",
              ],
              repeatNewPassword: [
                ["match", "newPassword"],
                this.props.passwordInitialized ? "optional" : "required",
              ],
            }}
            onFormChange={this.onPasswordFormChange}
            onSubmit={this.onSubmitPassword}
            clear={this.state.isModalOpen}
          >
            <FormGroup>
              <Label>
                <Msg id="form.password" />
              </Label>
              <Input type="password" name="password" size={6} />
            </FormGroup>
            {!this.props.passwordInitialized && (
              <FormGroup>
                <FormGroup>
                  <Label>
                    <Msg id="form.newPassword" />
                  </Label>
                  <Input type="password" name="newPassword" size={6} />
                </FormGroup>
                <FormGroup>
                  <Label>
                    <Msg id="form.confirmPassword" />
                  </Label>
                  <Input type="password" name="repeatNewPassword" size={6} />
                </FormGroup>
              </FormGroup>
            )}
            <Msg id="editor.iddelivery.forgotPassword" />
          </Form>
        </Modal>
      </div>
    );
  }

  private onSendId = () => {
    this.props.sendId(this.props.initial.urlToken, this.state.id);
  };

  private onFormChange = (
    formValid: boolean,
    formValues: { id: string },
    formErrors: {},
  ) => {
    this.setState({
      formValid: formValid || !formErrors,
      id: formValues.id,
    });
  };

  private onPasswordFormChange = (formValid: boolean, formValues: {}) => {
    this.setState({
      passwordFormValid: formValid,
      passwordValues: formValues,
    });
  };

  private onSubmitPassword = () => {
    if (this.props.passwordInitialized) {
      const params = {
        id: this.state.id,
        password: (this.state.passwordValues as { password: string }).password,
      };

      this.props.signin(this.props.initial.urlToken, params);
    } else {
      const params = {
        id: this.state.id,
        ...this.state.passwordValues,
      };

      this.props.setPassword(this.props.initial.urlToken, params);
    }
  };

  private onCloseModal = () => {
    this.setState({
      isModalOpen: false,
    });
  };
}

export default ExamEntranceId;
